import React, { useState, useEffect, useCallback } from 'react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { merchantApi } from '../api/config';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faHandHoldingDollar, faPlus, faCoins, faLock, faClock,
  faCheckCircle, faTimesCircle, faBan, faHourglassHalf, faArrowUp, faArrowDown,
  faSearch, faTimes, faDownload, faRotate,
} from '@fortawesome/free-solid-svg-icons';
import { UiPageHeader, UiButton, UiModal, UiStatsGrid, UiInput, UiSelect, UiFilterPanel, StatusBadge } from '../components/ui';
import { bankList } from '../utils/bankList';
import socketService from '../services/socketService';
import type { StatCardDef, StatusDef } from '../components/ui';
import DataTable from '../components/shared/DataTable';
import type { Column } from '../components/shared/DataTable';
import CountdownTimer from '../components/shared/CountdownTimer';

dayjs.extend(utc);
dayjs.extend(timezone);
const TZ = 'Asia/Bangkok';

interface P2pPayout {
  id: string;
  payout_number: string;
  amount: number;
  allocated_amount: number;
  funded_amount: number;
  remaining: number;
  status: string;
  bank_account_name: string;
  bank_account_number: string;
  bank_name: string;
  window_seconds_left: number;
  window_expires_at?: string;
  payout_fee?: number;
  payout_fee_pct?: number;
  created_at: string;
}

interface FeeWallet {
  balance: number;
  total_credit: number;
  total_debit: number;
}

// statusMap ระดับ module (ตาม DESIGN.md)
const STATUS_MAP: Record<string, StatusDef> = {
  pending: { bg: 'bg-amber-100 dark:bg-amber-500/15', text: 'text-amber-700 dark:text-amber-400', icon: faHourglassHalf, label: 'รอจับคู่' },
  p2p_completed: { bg: 'bg-green-100 dark:bg-green-500/15', text: 'text-green-700 dark:text-green-400', icon: faCheckCircle, label: 'สำเร็จ' },
  p2p_partial: { bg: 'bg-indigo-100 dark:bg-indigo-500/15', text: 'text-indigo-700 dark:text-indigo-400', icon: faClock, label: 'รอเว็บโอนส่วนที่เหลือ' },
  pending_transfer: { bg: 'bg-blue-100 dark:bg-blue-500/15', text: 'text-blue-700 dark:text-blue-400', icon: faClock, label: 'รอยืนยันการโอน' },
  expired: { bg: 'bg-gray-100 dark:bg-slate-700', text: 'text-gray-600 dark:text-slate-300', icon: faTimesCircle, label: 'หมดอายุ' },
  cancelled: { bg: 'bg-gray-100 dark:bg-slate-700', text: 'text-gray-600 dark:text-slate-300', icon: faBan, label: 'ยกเลิก' },
};

const fmtBaht = (n: number) => `฿${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

// สถานะ deposit ที่เติมเข้า payout (แสดงในหน้ารายละเอียด)
const FUNDING_STATUS: Record<string, { label: string; cls: string }> = {
  paid: { label: 'เติมแล้ว', cls: 'text-green-600 dark:text-green-400' },
  pending: { label: 'กำลังโอน', cls: 'text-amber-600 dark:text-amber-400' },
  expired_locked: { label: 'ค้างรอแอดมิน', cls: 'text-gray-500 dark:text-slate-400' },
};

interface P2pFunding { order_number: string; amount: number; status: string; at: string; }
interface P2pPayoutDetail { payout_number: string; amount: number; funded_amount: number; allocated_amount: number; status: string; fundings: P2pFunding[]; }

const P2pPayoutsPage: React.FC = () => {
  const [payouts, setPayouts] = useState<P2pPayout[]>([]);
  const [fee, setFee] = useState<FeeWallet | null>(null);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [total, setTotal] = useState(0);

  // ── ค้นหา (debounce) + กรองวันที่ (default = วันนี้) ──
  const today = dayjs().tz(TZ).format('YYYY-MM-DD');
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFrom, setDateFrom] = useState(today);
  const [dateTo, setDateTo] = useState(today);
  const [appliedDateFrom, setAppliedDateFrom] = useState(today);
  const [appliedDateTo, setAppliedDateTo] = useState(today);

  const [showCreate, setShowCreate] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [form, setForm] = useState({ amount: '', bank_account_name: '', bank_account_number: '', bank_name: '' });

  // ── รายละเอียดยอดที่เติมเข้ามา (คลิก row) ──
  const [detail, setDetail] = useState<P2pPayout | null>(null);
  const [detailData, setDetailData] = useState<P2pPayoutDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const openDetail = useCallback(async (p: P2pPayout) => {
    setDetail(p);
    setDetailData(null);
    setDetailLoading(true);
    try {
      const res = await merchantApi.get(`/merchants/p2p-payouts/${p.id}/fundings`);
      if (res.data.success) setDetailData(res.data.data);
    } catch (e: any) {
      toast.error(e.response?.data?.message || 'โหลดรายละเอียดไม่สำเร็จ');
    } finally {
      setDetailLoading(false);
    }
  }, []);

  useEffect(() => {
    const delay = setTimeout(() => { setSearchTerm(searchInput); setCurrentPage(1); }, 400);
    return () => clearTimeout(delay);
  }, [searchInput]);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append('limit', String(pageSize));
      params.append('offset', String((currentPage - 1) * pageSize));
      if (searchTerm) params.append('search', searchTerm);
      if (appliedDateFrom) params.append('start_date', appliedDateFrom);
      if (appliedDateTo) params.append('end_date', appliedDateTo);
      const [pRes, fRes] = await Promise.all([
        merchantApi.get(`/merchants/p2p-payouts?${params.toString()}`),
        merchantApi.get('/merchants/p2p-fee-wallet'),
      ]);
      if (pRes.data.success) {
        setPayouts(pRes.data.data || []);
        setTotal(pRes.data.total || 0);
      }
      if (fRes.data.success) setFee(fRes.data.data);
    } catch (e: any) {
      toast.error(e.response?.data?.message || 'โหลดข้อมูลไม่สำเร็จ');
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, searchTerm, appliedDateFrom, appliedDateTo]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleSearch = () => {
    if (dayjs(dateFrom).isAfter(dayjs(dateTo), 'day')) { toast.error('วันที่เริ่มต้นต้องน้อยกว่าหรือเท่ากับวันที่สิ้นสุด'); return; }
    setAppliedDateFrom(dateFrom);
    setAppliedDateTo(dateTo);
    setCurrentPage(1);
  };

  const handleReset = () => {
    setDateFrom(today); setDateTo(today);
    setAppliedDateFrom(today); setAppliedDateTo(today);
    setSearchInput(''); setSearchTerm('');
    setCurrentPage(1);
  };

  // Export CSV — ดึงทุกแถวตาม filter (ไม่ตัดหน้า) แล้ว gen ฝั่ง client (pattern เดียวกับหน้า Payouts)
  const exportCSV = async () => {
    try {
      setExporting(true);
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      if (appliedDateFrom) params.append('start_date', appliedDateFrom);
      if (appliedDateTo) params.append('end_date', appliedDateTo);
      const res = await merchantApi.get(`/merchants/p2p-payouts/export?${params.toString()}`);
      const rows: (P2pPayout & { completed_at?: string })[] = res.data.data || [];
      if (!rows.length) { toast.warning('ไม่มีข้อมูลสำหรับออกรายงาน'); return; }

      const headers = [
        'เลขที่รายการถอน', 'จำนวนเงิน', 'เติมแล้ว', 'จองค้าง', 'คงเหลือ',
        'ค่าธรรมเนียม', 'อัตราค่าธรรมเนียม(%)', 'สถานะ',
        'ธนาคาร', 'เลขบัญชี', 'ชื่อบัญชี', 'วันที่สร้าง', 'วันที่สำเร็จ',
      ];
      const esc = (v: any) => {
        const s = v == null ? '' : String(v);
        return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
      };
      const fmt = (d?: string) => d ? dayjs(d).tz(TZ).format('DD/MM/YYYY HH:mm:ss') : '';
      const statusLabel = (s: string) => STATUS_MAP[s]?.label || s;

      const csvLines = [
        headers.join(','),
        ...rows.map((p) => [
          esc(p.payout_number),
          esc(p.amount),
          esc(p.funded_amount),
          esc(p.allocated_amount),
          esc(p.remaining),
          esc(p.payout_fee ?? ''),
          esc(p.payout_fee_pct ?? ''),
          esc(statusLabel(p.status)),
          esc(p.bank_name),
          esc(p.bank_account_number),
          esc(p.bank_account_name),
          esc(fmt(p.created_at)),
          esc(fmt(p.completed_at)),
        ].join(',')),
      ];
      const blob = new Blob(['﻿' + csvLines.join('\n')], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `p2p_payouts_${appliedDateFrom}_${appliedDateTo}.csv`;
      link.click();
      URL.revokeObjectURL(link.href);
    } catch (e: any) {
      toast.error(e.response?.data?.message || 'ออกรายงานไม่สำเร็จ');
    } finally {
      setExporting(false);
    }
  };

  // ⭐ realtime: peer เติมยอด/จับคู่/ปิดงาน/ใบใหม่จาก API → refresh (backend ยิง payout-created/updated เข้าห้อง merchant แล้ว)
  useEffect(() => {
    // ⭐ กรองเฉพาะ event ของ P2P liquidity — payout ปกติของร้านยิง event เดียวกัน ไม่เกี่ยวกับหน้านี้
    const refresh = (data: any) => { if (data?.is_p2p_liquidity !== true) return; loadData(); };
    socketService.on('payout-created', refresh);
    socketService.on('payout-updated', refresh);
    return () => {
      socketService.off('payout-created', refresh);
      socketService.off('payout-updated', refresh);
    };
  }, [loadData]);

  const setField = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => setForm({ ...form, [k]: e.target.value });

  const handleCreate = async () => {
    const amt = Number(form.amount);
    if (!amt || amt <= 0) { toast.error('กรุณาระบุจำนวนเงิน'); return; }
    if (!form.bank_account_name || !form.bank_account_number || !form.bank_name) {
      toast.error('กรุณากรอกบัญชีรับเงินให้ครบ'); return;
    }
    try {
      setSubmitting(true);
      const res = await merchantApi.post('/merchants/p2p-payouts', {
        amount: amt,
        bank_account_name: form.bank_account_name,
        bank_account_number: form.bank_account_number,
        bank_name: form.bank_name,
      });
      if (res.data.success) {
        toast.success('สร้าง P2P payout สำเร็จ');
        setShowCreate(false);
        setForm({ amount: '', bank_account_name: '', bank_account_number: '', bank_name: '' });
        loadData();
      }
    } catch (e: any) {
      toast.error(e.response?.data?.message || 'สร้างไม่สำเร็จ');
    } finally {
      setSubmitting(false);
    }
  };

  const statCards: StatCardDef[] = [
    {
      icon: faCoins, iconClassName: 'bg-green-50 dark:bg-green-900/30 text-green-600 dark:text-green-400',
      label: 'เครดิตค่าธรรมเนียม P2P',
      value: <span className="text-gray-800 dark:text-slate-100">{fmtBaht(fee?.balance || 0)}</span>,
      subValue: <span className="text-xs text-gray-400 dark:text-slate-500">กระเป๋า fee (เติมล่วงหน้า)</span>,
    },
    {
      icon: faArrowDown, iconClassName: 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400',
      label: 'เติมสะสม',
      value: <span className="text-gray-800 dark:text-slate-100">{fmtBaht(fee?.total_credit || 0)}</span>,
    },
    {
      icon: faArrowUp, iconClassName: 'bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400',
      label: 'หักสะสม (ค่าธรรมเนียม)',
      value: <span className="text-gray-800 dark:text-slate-100">{fmtBaht(fee?.total_debit || 0)}</span>,
    },
  ];

  const columns: Column<P2pPayout>[] = [
    {
      key: 'payout_number', header: 'เลขที่',
      // แสดงเลขเต็มไม่ตัด
      render: (p) => <code className="text-xs whitespace-nowrap bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-slate-200 px-2 py-1 rounded">{p.payout_number}</code>,
    },
    {
      key: 'amount', header: 'ยอดถอน', width: '120px',
      render: (p) => <span className="font-bold text-sm tabular-nums" style={{ color: 'var(--theme-accent)' }}>{fmtBaht(p.amount)}</span>,
    },
    {
      key: 'funded', header: 'เติมแล้ว / เหลือ', width: '170px',
      render: (p) => {
        const amount = Number(p.amount) || 0;
        const funded = Number(p.funded_amount || 0);
        const allocated = Number(p.allocated_amount || 0);
        const remaining = Math.max(0, Number(p.remaining ?? amount - funded - allocated));
        const pct = (v: number) => (amount > 0 ? Math.min(100, (v / amount) * 100) : 0);
        return (
          <div className="min-w-[140px]">
            <div className="flex h-2 w-full overflow-hidden rounded-full bg-gray-100 dark:bg-slate-700">
              <div className="bg-green-500 transition-all" style={{ width: `${pct(funded)}%` }} title={`เติมแล้ว ${fmtBaht(funded)}`} />
              <div className="bg-amber-400 transition-all" style={{ width: `${pct(allocated)}%` }} title={`รอยืนยัน ${fmtBaht(allocated)}`} />
            </div>
            <div className="mt-1 flex justify-between text-[10px] tabular-nums leading-tight">
              <span className="text-green-600 dark:text-green-400">เติม {fmtBaht(funded)}</span>
              <span className="text-gray-500 dark:text-slate-400">เหลือ {fmtBaht(remaining)}</span>
            </div>
          </div>
        );
      },
    },
    {
      key: 'fee', header: 'ค่าธรรมเนียม', width: '130px',
      render: (p) => (
        <span className="text-xs tabular-nums text-amber-600 dark:text-amber-400">
          {fmtBaht(p.payout_fee || 0)} <span className="text-gray-400 dark:text-slate-500">({p.payout_fee_pct ?? 0}%)</span>
        </span>
      ),
    },
    {
      key: 'bank', header: 'บัญชีรับ',
      render: (p) => (
        <div className="text-xs leading-tight">
          <p className="font-medium text-gray-800 dark:text-slate-100">{p.bank_account_name}</p>
          <p className="text-gray-400 dark:text-slate-500">{p.bank_name} · {p.bank_account_number}</p>
        </div>
      ),
    },
    {
      key: 'window', header: 'หมดเวลาใน', width: '120px',
      render: (p) => (
        p.status === 'pending' && p.window_expires_at
          ? <span className="tabular-nums text-sm font-medium" style={{ color: 'var(--theme-accent)' }}><FontAwesomeIcon icon={faClock} className="mr-1" /><CountdownTimer expiresAt={p.window_expires_at} /></span>
          : <span className="text-gray-300 dark:text-slate-600">—</span>
      ),
    },
    {
      key: 'status', header: 'สถานะ', width: '180px',
      render: (p) => (
        <div className="flex items-center gap-1.5">
          {(p.allocated_amount || 0) > 0 && <FontAwesomeIcon icon={faLock} className="text-[10px] text-amber-500" title="มี deposit จองค้าง" />}
          <StatusBadge status={p.status} statusMap={STATUS_MAP} />
        </div>
      ),
    },
  ];

  return (
    <>
      <UiPageHeader
        icon={faHandHoldingDollar}
        title="รับสภาพคล่อง (P2P)"
        subtitle={`สร้างรายการถอนเพื่อรับเงินจากผู้ฝาก P2P โดยตรง • ${total.toLocaleString()} รายการ`}
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            <UiButton variant="secondary" icon={exporting ? faRotate : faDownload} onClick={exportCSV} disabled={exporting || payouts.length === 0}>
              {exporting ? 'กำลังออกรายงาน...' : 'Export CSV'}
            </UiButton>
            <UiButton icon={faPlus} onClick={() => setShowCreate(true)}>สร้างรายการถอน P2P</UiButton>
          </div>
        }
      />

      <UiStatsGrid cards={statCards} loading={loading && !fee} cols={3} />

      <div className="mt-4">
        <UiFilterPanel
          dateFrom={dateFrom}
          dateTo={dateTo}
          onDateFromChange={setDateFrom}
          onDateToChange={setDateTo}
          onSearch={handleSearch}
          onReset={handleReset}
          loading={loading}
        >
          <div className="flex-1 min-w-[220px]">
            <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-1"><FontAwesomeIcon icon={faSearch} className="mr-1" />ค้นหา</label>
            <div className="relative">
              <input
                type="text" placeholder="เลขที่รายการถอน..." value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-200 dark:focus:ring-slate-600 focus:border-gray-400 dark:focus:border-slate-500 pr-7"
              />
              {searchInput && <button type="button" onClick={() => setSearchInput('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-red-500"><FontAwesomeIcon icon={faTimes} className="text-xs" /></button>}
            </div>
          </div>
        </UiFilterPanel>

        <DataTable
          data={payouts}
          columns={columns}
          loading={loading}
          onRowClick={openDetail}
          serverPagination={{
            currentPage,
            totalItems: total,
            pageSize,
            onPageChange: setCurrentPage,
            onPageSizeChange: setPageSize,
          }}
        />
      </div>

      <UiModal
        open={showCreate}
        onClose={() => setShowCreate(false)}
        title="สร้างรายการถอน P2P"
        icon={faHandHoldingDollar}
        size="md"
        footer={
          <div className="flex gap-3">
            <UiButton variant="secondary" onClick={() => setShowCreate(false)} disabled={submitting} className="flex-1 justify-center">ยกเลิก</UiButton>
            <UiButton onClick={handleCreate} loading={submitting} className="flex-1 justify-center">ยืนยัน</UiButton>
          </div>
        }
      >
        <div className="space-y-4">
          <UiInput label="จำนวนเงิน" required prefix="฿" type="number" name="amount"
            value={form.amount} onChange={setField('amount')} placeholder="0.00" />
          <UiInput label="ชื่อบัญชีผู้รับ" required name="bank_account_name"
            value={form.bank_account_name} onChange={setField('bank_account_name')} placeholder="ชื่อ-นามสกุล" />
          <UiInput label="เลขบัญชีผู้รับ" required name="bank_account_number"
            value={form.bank_account_number} onChange={setField('bank_account_number')} placeholder="เลขบัญชี" />
          <UiSelect label="ธนาคาร" required name="bank_name"
            value={form.bank_name} onChange={setField('bank_name')}>
            <option value="">— เลือกธนาคาร —</option>
            {bankList.filter((b) => b.isActive).map((b) => (
              <option key={b.bankCode} value={b.shortName}>{b.shortName} - {b.bankName}</option>
            ))}
          </UiSelect>
          <p className="text-xs text-gray-400 dark:text-slate-500">
            รายการนี้มีอายุ 15 นาที (รีเซ็ตทุกครั้งที่มีคนฝากเข้ามาจับคู่) · หักค่าธรรมเนียมจากกระเป๋า P2P เมื่อรับเงินครบ
          </p>
        </div>
      </UiModal>

      <UiModal
        open={!!detail}
        onClose={() => setDetail(null)}
        title="รายละเอียดยอดที่เติมเข้ามา"
        icon={faCoins}
        size="md"
      >
        {detail && (
          <div className="space-y-3 text-sm">
            <div className="flex flex-wrap items-center justify-between gap-2 bg-gray-50 dark:bg-slate-700/50 rounded-xl p-3">
              <code className="text-xs bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-slate-200 px-2 py-1 rounded">{detail.payout_number}</code>
              <span className="text-xs text-gray-500 dark:text-slate-400">
                เติมแล้ว <strong className="text-green-600 dark:text-green-400 tabular-nums">{fmtBaht(detailData?.funded_amount ?? detail.funded_amount)}</strong>
                {' / '}<span className="tabular-nums">{fmtBaht(detail.amount)}</span>
              </span>
            </div>

            {detailLoading ? (
              <p className="text-center text-gray-400 dark:text-slate-500 py-6">กำลังโหลด...</p>
            ) : !detailData?.fundings?.length ? (
              <p className="text-center text-gray-400 dark:text-slate-500 py-6">ยังไม่มีรายการเติมเข้ามา</p>
            ) : (
              <div className="divide-y divide-gray-100 dark:divide-slate-700 rounded-xl border border-gray-100 dark:border-slate-700 overflow-hidden">
                {detailData.fundings.map((f, i) => {
                  const st = FUNDING_STATUS[f.status] || { label: f.status, cls: 'text-gray-500' };
                  return (
                    <div key={i} className="flex items-center justify-between px-3 py-2.5">
                      <div className="flex items-center gap-2">
                        <span className="font-bold tabular-nums" style={{ color: 'var(--theme-accent)' }}>{fmtBaht(f.amount)}</span>
                        <span className={`text-[11px] font-medium ${st.cls}`}>{st.label}</span>
                      </div>
                      <span className="text-xs text-gray-400 dark:text-slate-500 tabular-nums">{dayjs(f.at).tz(TZ).format('DD/MM/YY HH:mm')}</span>
                    </div>
                  );
                })}
              </div>
            )}
            <p className="text-[11px] text-gray-400 dark:text-slate-500">แสดงเฉพาะรายการที่เติม/กำลังโอน/ค้างรอแอดมิน</p>
          </div>
        )}
      </UiModal>
    </>
  );
};

export default P2pPayoutsPage;
